import { FC } from 'react';

import classNames from 'classnames';
import { DOT } from 'utils/constants/common';

import {
    Cell,
    HSpacing,
    Radio,
    TableBody,
    TableCell,
    TableRow,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { setIsVacancyForeignArea } from 'store/autoReducers';
import {
    selectWizardSelectedVacancyId,
    selectWizardVacancies,
    setWizardSelectedVacancy,
    WizardVacancy,
} from 'store/models/campaignCreationWizard';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignCreationWizard/VacancySelectStep/components/VacancyListTab/WizardVacanciesTable/WizardVacanciesTableBody/WizardVacanciesTableBody.module.less';

const TrlKeys = {
    id: 'CampaignCreationWizard.VacancySelectStep.table.vacancy.id',
};

interface WizardVacanciesTableBodyProps {
    paddingLeft?: number;
    paddingRight?: number;
}

export const WIZARD_TABLE_COLUMN_WIDTH = {
    area: 240,
    manager: 360,
};

export const WizardVacanciesTableBody: FC<WizardVacanciesTableBodyProps> = ({ paddingLeft = 0, paddingRight = 0 }) => {
    const dispatch = useDispatch();
    const trl = useTrls();
    const { isMobile } = useBreakpoint();

    const vacancies = useSelector(selectWizardVacancies);
    const selectedVacancyId = useSelector(selectWizardSelectedVacancyId);

    const handleSelect = (vacancy: WizardVacancy) => {
        dispatch([
            setWizardSelectedVacancy({
                vacancyId: vacancy.vacancyId,
                vacancyName: vacancy.name,
                foreignArea: vacancy.foreignArea,
            }),
            setIsVacancyForeignArea(vacancy.foreignArea),
        ]);
    };

    const firstCell = (vacancy: WizardVacancy) => {
        const { vacancyId, name, areaName } = vacancy;
        return (
            <Cell
                vertPadding={isMobile}
                align="top"
                left={
                    <Radio
                        name="wizard-vacancy"
                        value={String(vacancyId)}
                        checked={selectedVacancyId === vacancyId}
                        onChange={() => undefined}
                        data-qa={`wizard-vacancy-radio-${vacancyId}`}
                    />
                }
            >
                <Text maxLines={1} typography="label-2-regular" data-qa="wizard-vacancy-name">
                    {name}
                </Text>
                <VSpacing default={4} />
                <Text maxLines={1} typography="label-3-regular" style="secondary">
                    {trl(TrlKeys.id, [vacancyId])}
                    {isMobile && (
                        <>
                            <HSpacing default={4} />
                            {DOT}
                            <HSpacing default={4} />
                            {areaName}
                        </>
                    )}
                </Text>
            </Cell>
        );
    };

    if (isMobile) {
        return (
            <>
                {vacancies.map((vacancy, index) => (
                    <div
                        key={vacancy.vacancyId}
                        className={classNames(styles.mobileRow, { [styles.mobileRowFirst]: index === 0 })}
                        style={{ paddingLeft, paddingRight }}
                        data-qa={`wizard-vacancy-mobile-row-${vacancy.vacancyId}`}
                        onClick={() => handleSelect(vacancy)}
                    >
                        {firstCell(vacancy)}
                    </div>
                ))}
            </>
        );
    }

    return (
        <TableBody>
            {vacancies.map((vacancy) => (
                <TableRow
                    key={vacancy.vacancyId}
                    data-qa={`wizard-vacancy-row-${vacancy.vacancyId}`}
                    onClick={() => handleSelect(vacancy)}
                >
                    <TableCell>{firstCell(vacancy)}</TableCell>
                    <TableCell width={WIZARD_TABLE_COLUMN_WIDTH.area}>
                        <Text maxLines={1} data-qa="wizard-area-name">
                            {vacancy.areaName}
                        </Text>
                    </TableCell>
                    <TableCell width={WIZARD_TABLE_COLUMN_WIDTH.manager}>
                        <Text maxLines={1} data-qa="wizard-manager-name">
                            {vacancy.managerName}
                        </Text>
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    );
};
